import React, { useEffect, useState, Suspense, lazy, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import ProductInfo from "../../components/pageProps/productDetails/ProductInfo";
import ProductsOnSale from "../../components/pageProps/productDetails/ProductsOnSale";
import { loadProduct, paginationItems6 } from "../../constants/products";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Autoplay, Navigation } from "swiper";
import 'swiper/css';
import 'swiper/css/navigation';
import 'react-medium-image-zoom/dist/styles.css';

SwiperCore.use([Autoplay, Navigation]);
const Zoom = lazy(() => import('react-medium-image-zoom'));

const ProductDetails = () => {
  const location = useLocation();
  const params = useParams();

  const [productInfo, setProductInfo] = useState({});
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.item) setProductInfo(location.state.item);
    else if (params._id) setProductInfo({ codigo: params._id, Descripcion: "" });
  }, [location, params._id]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productInfo.codigo) return;
      setLoading(true);
      try {
        const cod = productInfo.codigo.replace(/\//g, "|");
        await loadProduct(cod);
        const fetchedProduct = paginationItems6[0];
        setProduct(fetchedProduct);
        setActiveImage(fetchedProduct.img);
      } catch (error) {
        console.error("Error al cargar el producto:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productInfo]);

  const handleImageClick = (imgUrl) => setActiveImage(imgUrl);

  const productImages = useMemo(() => (product?.prod_imagenes || []).map(imgObj => imgObj.rimagen).filter(Boolean), [product]);

  if (loading) return <div className="text-center p-10">Cargando producto...</div>;
  if (!product) return <div className="text-center p-10">Producto no encontrado</div>;

  return (
    <div className="w-full mx-auto border-b-[1px] border-b-gray-300">

      {/* CSS directo para tamaño fijo con fill */}
      <style>
        {`
          .img-fijo {
            width: 350px;
            height: 350px;
            object-fit: fill;
            object-position: center;
            display: block;
          }
          .thumbnail-fijo {
            width: 96px; /* miniaturas fijas */
            height: 96px;
            object-fit: fill;
            object-position: center;
            cursor: pointer;
          }
        `}
      </style>

      <div className="max-w-container mx-auto px-4">
        <div className="w-full flex flex-col gap-8 h-full pb-10 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 w-full">

            {/* IMAGEN PRINCIPAL */}
            <div className="flex flex-col items-center">
              <Suspense fallback={<div>Cargando zoom...</div>}>
                <Zoom>
                  <img
                    className="img-fijo rounded-md shadow-md"
                    src={activeImage}
                    alt={product?.Descripcion}
                    loading="lazy"
                  />
                </Zoom>
              </Suspense>

              {/* MINIATURAS */}
              {productImages.length > 0 && (
                productImages.length <= 4 ? (
                  <div className="flex gap-4 mt-4">
                    {productImages.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`thumbnail-${index}`}
                        loading="lazy"
                        className="thumbnail-fijo p-1 rounded-md shadow-sm"
                        onClick={() => handleImageClick(img)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 w-full px-4 relative">
                    <Swiper
                      modules={[Autoplay, Navigation]}
                      spaceBetween={16}
                      slidesPerView={4}
                      allowTouchMove={false}
                      autoplay={{ delay: 3000, disableOnInteraction: false }}
                      navigation={{ prevEl: ".prevBtn", nextEl: ".nextBtn" }}
                    >
                      {productImages.map((img, index) => (
                        <SwiperSlide key={index}>
                          <img
                            src={img}
                            alt={`thumbnail-${index}`}
                            loading="lazy"
                            className="thumbnail-fijo p-1 rounded-md"
                            onClick={() => handleImageClick(img)}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <button className="prevBtn absolute top-1/2 left-0 transform -translate-y-1/2 bg-white p-2 shadow-md z-10">⬅</button>
                    <button className="nextBtn absolute top-1/2 right-0 transform -translate-y-1/2 bg-white p-2 shadow-md z-10">➡</button>
                  </div>
                )
              )}
            </div>

            {/* INFO PRODUCTO */}
            <div className="flex flex-col gap-4">
              <ProductInfo productInfo={product} />
            </div>

            {/* DETALLES ADICIONALES */}
            {product?.datos_adicionales && (
              <div className="flex flex-col gap-4">
                <h2 className="text-xl font-semibold">Detalles Adicionales</h2>
                <div
                  className="text-gray-700 p-4"
                  dangerouslySetInnerHTML={{ __html: product.datos_adicionales }}
                ></div>
              </div>
            )}

          </div>

          {/* PRODUCTOS RELACIONADOS */}
          {product?.productos_relacionados?.length > 0 && (
            <div className="w-full mt-10">
              <ProductsOnSale relatedProducts={product.productos_relacionados} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
